import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import {
  SparklesIcon,
  CubeTransparentIcon,
  ChevronDownIcon,
  ArrowRightIcon,
  PlayIcon,
  PauseIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Button } from '../components/Button';
import { isSignedIn, signup, handleGoogleSignIn } from '../auth';
import { GoogleLogin } from '@react-oauth/google';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import '@google/model-viewer';
import { useSEO } from '../hooks/useSEO';

// Curated examples from AiStudioPaywall.jsx
const CURATED_EXAMPLES = [
  {
    id: 'env-asset',
    prompt: "A collection of stylized pine trees, low polygon count, gradient green textures, snowy tips, suitable for a mobile strategy game map.",
    image: "https://files.compress-glb.com/examples/the-environment-asset/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-environment-asset/the-environment-asset.glb"
  },
  {
    id: 'fashion',
    prompt: "Futuristic high-top sneaker, chunky sole, holographic white leather material, neon blue accents, streetwear style, clean commercial render.",
    image: "https://files.compress-glb.com/examples/the-fashion-item/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-fashion-item/the-fashion-item.glb"
  },
  {
    id: 'mobile-char',
    prompt: "A cute blocky robot helper, voxel art style, orange and white color scheme, simple geometry, floating antennas, vibrant colors.",
    image: "https://files.compress-glb.com/examples/the-mobile-game-character/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-mobile-game-character/the-mobile-game-character.glb"
  },
  {
    id: 'scifi',
    prompt: "Worn astronaut helmet, gold reflective visor, white tactical padding, scratches and battle damage, realistic sci-fi prop, Unreal Engine 5 style.",
    image: "https://files.compress-glb.com/examples/the-sci-fi-prop/Front_view.png",
    model_url: "https://files.compress-glb.com/examples/the-sci-fi-prop/the-sci-fi-prop.glb"
  }
];

const FAQ_ITEMS = [
  {
    question: "How do I generate a GLB from text?",
    answer: "Simply describe your object in the prompt field above (e.g., 'A vintage leather suitcase'). Our AI analyzes the semantic meaning and constructs a 3D mesh with appropriate geometry and PBR textures, then exports it as a GLB file ready for download."
  },
  {
    question: "Can I use these assets in React Three Fiber / Three.js?",
    answer: "Yes. The generated GLB/GLTF files are standard-compliant. They work seamlessly in Three.js, React Three Fiber, Babylon.js, Unity, Unreal Engine, and web-based AR viewers like <model-viewer>."
  },
  {
    question: "Is the topology optimized for web?",
    answer: "Absolutely. Our generator produces efficient geometry suitable for real-time rendering in games and web experiences."
  },
  {
    question: "What about commercial rights?",
    answer: "You own the assets you generate. You have full commercial rights to use them in games, websites, marketing materials, or resell them on marketplaces."
  },
  {
    question: "Can I convert images to 3D models?",
    answer: "Yes. In the AI Studio, you can upload a reference image. Our Image-to-3D pipeline interprets depth and form to create a matching 3D model."
  },
  {
    question: "Do the models come with UV maps?",
    answer: "Yes, all generated models include automatic UV unwrapping. The texture map is correctly mapped to the geometry, making them ready for editing in tools like Blender or Substance Painter."
  },
  {
    question: "Are the models rigged or animated?",
    answer: "Currently, we generate static meshes only. The models do not come with skeletons, rigs, or animations. However, the topology is clean enough to be auto-rigged using third-party tools like Mixamo or AccuRIG."
  },
  {
    question: "What is the typical polycount?",
    answer: "Most generations fall between 20,000 and 100,000 triangles, which is the sweet spot for real-time rendering in games and web experiences. The geometry is watertight and optimized for performance."
  },
  {
    question: "Can I edit the models in Blender?",
    answer: "Yes! Since we export standard .glb files, you can import them directly into Blender, Maya, Cinema 4D, or 3ds Max for further editing, sculpting, or retopology."
  },
  {
    question: "What is the texture resolution?",
    answer: "We generate high-quality 2K (2048x2048) texture maps. This ensures your assets look crisp even when viewed up close in AR or VR environments."
  },
  {
    question: "Can I use these for 3D printing?",
    answer: "Generally, yes. The geometry is generated to be watertight (manifold). However, depending on the complexity of the shape (e.g., thin floating parts), you might need to add supports or minor fixes in slicer software before printing."
  },
  {
    question: "Is there a free trial?",
    answer: "We do not currently offer a free trial, but our Starter plan is designed to be affordable for testing the capabilities of our studio."
  },
  {
    question: "How does the credit system work?",
    answer: "Each generation costs a specific amount of credits (typically 15 credits for a text-to-3D or image-to-3D task). You can subscribe for a monthly allowance to receive credits."
  },
  {
    question: "Can I cancel my subscription anytime?",
    answer: "Yes, you can cancel your subscription at any time from your dashboard. You will retain access to your remaining credits until the end of the billing period."
  },
  {
    question: "Do you offer an API for developers?",
    answer: "We are working on a public API. If you are an enterprise looking to integrate our generation pipeline into your application, please contact our sales team for early access."
  },
  {
    question: "What happens if the generation fails?",
    answer: "Our system automatically detects failed generations. Credits are only deducted for successful generations."
  },
  {
    question: "Are the models compatible with AR on iOS (USDZ)?",
    answer: "Our primary export is GLB (Android/Web standard). However, GLB files can be easily converted to USDZ for iOS AR Quick Look using free online tools."
  },
  {
    question: "How long does generation take?",
    answer: "Most models are generated in under 60 seconds. Complex prompts or high server load may occasionally extend this to 2-3 minutes."
  },
  {
    question: "Can I generate full environments or scenes?",
    answer: "Our model is currently optimized for single objects (props, furniture, characters, items). Generating complex scenes with multiple distinct objects in one prompt may result in lower quality or merged geometry."
  },
  {
    question: "Is my data private?",
    answer: "Yes. If you are on a Pro or Business plan, your generations are private by default. On the free tier, generations may be visible in the public community gallery."
  },
  {
    question: "How do I import into Unity?",
    answer: "Simply drag and drop the .glb file into your Unity Project window. Unity has native support for glTF/GLB files. For best results, ensure you are using a recent version of Unity or the glTFast package."
  },
  {
    question: "How do I import into Unreal Engine 5?",
    answer: "Unreal Engine 5 supports glTF import natively. You can import the .glb file via the Content Drawer > Import, or use the glTF Importer plugin for more advanced control over materials and scale."
  },
  {
    question: "What is Draco compression?",
    answer: "Draco is a library for compressing 3D geometry. It significantly reduces the file size of your 3D models (often by 90%+) by compressing vertex positions, normals, and texture coordinates, making them load much faster on the web."
  },
  {
    question: "Can I generate characters?",
    answer: "Yes, you can generate stylized or realistic characters. Please note that they are generated as static meshes in a standard 'A-pose' or 'T-pose' if specified, ready for you to rig and animate elsewhere."
  },
  {
    question: "Do you support transparent textures?",
    answer: "Our current pipeline focuses on opaque materials (metal, wood, plastic, etc.). Transparency (glass, water) is experimental and may not always render correctly in the initial generation."
  },
  {
    question: "Can I resell the models I generate?",
    answer: "Yes. You own full commercial rights to the assets you generate. You can sell them on marketplaces like Sketchfab, Unity Asset Store, or TurboSquid without owing us royalties."
  }
];

function VideoPlayer() {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [progress, setProgress] = useState(0);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const current = videoRef.current.currentTime;
      const duration = videoRef.current.duration;
      if (duration) {
        setProgress((current / duration) * 100);
      }
    }
  };

  return (
    <div className="relative">
      <div className="relative aspect-video rounded-3xl overflow-hidden border border-white/10 bg-black/40 shadow-2xl group">
        <video
          ref={videoRef}
          src="https://files.compress-glb.com/aistudio-example.mp4"
          className="w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          onTimeUpdate={handleTimeUpdate}
        />

        {/* Controls Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
          <div className="flex items-center gap-4 w-full">
            <button
              onClick={togglePlay}
              className="p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md text-white transition-colors"
            >
              {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5" />}
            </button>

            <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 transition-all duration-100 ease-linear"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-8 right-4 text-[10px] font-mono text-white/30 uppercase tracking-widest">
        Sped up 6x
      </div>
    </div>
  );
}

function ModelPreview({ model }) {
  const [show3D, setShow3D] = useState(false);

  return (
    <div className="group relative aspect-square rounded-2xl overflow-hidden bg-black/40 border border-white/10 cursor-pointer hover:border-purple-500/50 transition-all">
      {show3D ? (
        <div className="w-full h-full relative bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 via-gray-950 to-black">
          <model-viewer
            src={model.model_url}
            poster={model.image}
            alt={model.prompt}
            auto-rotate
            camera-controls
            shadow-intensity="1"
            shadow-softness="1"
            environment-image="neutral"
            exposure="1.0"
            style={{ width: '100%', height: '100%' }}
          >
            <div slot="poster" className="flex items-center justify-center w-full h-full bg-background-soft">
              <div className="w-16 h-16 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin"></div>
            </div>
          </model-viewer>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShow3D(false);
            }}
            className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full backdrop-blur-sm transition-colors z-10"
            title="Close 3D view"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div onClick={() => setShow3D(true)} className="w-full h-full relative">
          <img
            src={model.image}
            alt={model.prompt}
            className="w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-all duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity" />

          {/* Hover Overlay Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 transition-transform">
            <h3 className="text-white text-xs font-bold uppercase tracking-wider line-clamp-1 mb-1">{model.prompt}</h3>
            <div className="flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity delay-100">
              <div className="flex gap-2 text-[10px] text-white/60 font-mono">
                <span>GLB</span>
                <span>•</span>
                <span>2K TEXTURE</span>
              </div>
              <div className="flex items-center gap-1 text-[10px] font-bold text-purple-400 uppercase tracking-wider">
                <CubeTransparentIcon className="w-3 h-3" />
                <span>View 3D</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SignUpDialog({ isOpen, onClose, prompt, onSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await signup(email, password);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      await handleGoogleSignIn(credentialResponse, navigate);
      onSuccess();
    } catch (err) {
      setError('Google sign in failed');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-[#0f0f11] border border-white/10 p-8 text-left align-middle shadow-2xl transition-all">
                <div className="absolute top-4 right-4">
                  <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {success ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SparklesIcon className="w-8 h-8" />
                    </div>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-white mb-4">
                      Check your email
                    </Dialog.Title>
                    <p className="text-muted-foreground mb-8">
                      We've sent a verification link to <span className="text-white font-medium">{email}</span>.
                      Please verify your account to start generating.
                    </p>
                    <Button onClick={onClose} variant="primary" className="w-full">
                      Got it
                    </Button>
                  </div>
                ) : (
                  <>
                    <Dialog.Title as="h3" className="text-2xl font-bold text-white mb-2">
                      Create your account
                    </Dialog.Title>
                    <p className="text-muted-foreground mb-8 text-sm">
                      Join thousands of creators generating 3D assets with AI.
                    </p>

                    {error && (
                      <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                        {error}
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="flex justify-center w-full">
                        <GoogleLogin
                          onSuccess={handleGoogleSuccess}
                          onError={() => setError('Google Sign-In Failed')}
                          theme="filled_black"
                          shape="pill"
                          text="continue_with"
                          width="100%"
                        />
                      </div>

                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-white/10" />
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-[#0f0f11] px-2 text-muted-foreground">Or continue with email</span>
                        </div>
                      </div>

                      <form onSubmit={handleEmailSignUp} className="space-y-4">
                        <div>
                          <input
                            type="email"
                            required
                            placeholder="Email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                          />
                        </div>
                        <div>
                          <input
                            type="password"
                            required
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-all"
                          />
                        </div>
                        <Button
                          type="submit"
                          variant="primary"
                          className="w-full py-3 rounded-xl shadow-lg shadow-purple-500/20"
                          disabled={isLoading}
                        >
                          {isLoading ? 'Creating account...' : 'Create Account'}
                        </Button>
                      </form>

                      <p className="text-xs text-center text-muted-foreground mt-4">
                        By signing up, you agree to our Terms of Service and Privacy Policy.
                      </p>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default function GltfGeneratorPage() {
  const navigate = useNavigate();
  useSEO({
    title: 'AI GLB Generator – Create 3D Models from Text | Compress GLB',
    description: 'Generate production-ready GLB 3D models from a text prompt. Download standard glTF/GLB files that work in three.js, Babylon.js, Unity, Unreal and AR viewers.',
    canonical: '/gltf-generator',
    ogImage: '/images/og/gltf-generator.png',
    ogTitle: 'AI GLB Generator – Create 3D Models from Text',
    ogDescription: 'Generate production-ready GLB 3D models from a text prompt.',
  });
  const [prompt, setPrompt] = useState('');
  const [isSignUpOpen, setIsSignUpOpen] = useState(false);

  const handleGenerate = (e) => {
    e.preventDefault();

    const targetUrl = prompt.trim()
      ? `/app/ai-studio/create?prompt=${encodeURIComponent(prompt)}`
      : '/app/ai-studio/create';

    if (isSignedIn()) {
      navigate({ to: targetUrl });
    } else {
      setIsSignUpOpen(true);
    }
  };

  const handleSignUpSuccess = () => {
    const targetUrl = prompt.trim()
      ? `/app/ai-studio/create?prompt=${encodeURIComponent(prompt)}`
      : '/app/ai-studio/create';
    navigate({ to: targetUrl });
  };

  const handleCompressorRedirect = () => {
    navigate({ to: '/gltf-compressor' });
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground font-sans selection:bg-purple-500/30">
      <SignUpDialog
        isOpen={isSignUpOpen}
        onClose={() => setIsSignUpOpen(false)}
        prompt={prompt}
        onSuccess={handleSignUpSuccess}
      />

      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        {/* Background Gradients */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
          <div className="absolute top-20 left-20 w-96 h-96 bg-purple-600/20 rounded-full blur-[128px]" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/10 rounded-full blur-[128px]" />
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center relative z-10">

          {/* Left: Copy & Input */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-xs font-mono uppercase tracking-widest mb-6">
              <SparklesIcon className="w-4 h-4" />
              <span>AI-powered 3D synthesis</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.9] mb-6 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/60">
              Generate <br />
              production <br />
              ready GLB.
            </h1>

            <p className="text-lg text-muted-foreground mb-10 max-w-md leading-relaxed">
              Turn text and images into optimized 3D assets.
              Textured and ready for the web.
            </p>

            {/* The Magic Door Input */}
            <form onSubmit={handleGenerate} className="relative group max-w-lg">
              <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl opacity-20 group-hover:opacity-40 transition duration-500 blur-lg" />
              <div className="relative flex items-center bg-background border border-white/10 rounded-2xl p-2 shadow-2xl">
                <input
                  type="text"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="A futuristic cyberpunk helmet..."
                  className="flex-1 bg-transparent border-none focus:ring-0 text-white placeholder:text-white/30 px-4 py-3 text-lg"
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="rounded-xl px-6 py-3 shadow-lg shadow-purple-500/20"
                >
                  Generate
                </Button>
              </div>
            </form>
          </motion.div>

          {/* Right: Video Preview */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <VideoPlayer />
          </motion.div>
        </div>
      </section>

      {/* --- INTENT FILTER --- */}
      <div className="bg-white/5 border-y border-white/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
          <span className="text-muted-foreground">Looking to compress or convert existing files instead?</span>
          <button
            onClick={handleCompressorRedirect}
            className="text-white hover:text-purple-400 font-medium flex items-center gap-1 transition-colors"
          >
            Go to GLB compressor <ArrowRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* --- HOW IT WORKS (Horizontal) --- */}
      <section className="py-32 px-6 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[500px] bg-purple-900/20 blur-[120px] rounded-full pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="mb-20 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">From imagination to mesh.</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Our pipeline handles the complexity of 3D generation so you can focus on the creative direction.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting Line */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent border-t border-dashed border-white/20" />

            {/* Step 1 */}
            <div className="relative group">
              <div className="w-24 h-24 mx-auto bg-black/40 border border-white/10 rounded-2xl flex items-center justify-center mb-8 relative z-10 group-hover:border-purple-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.3)] transition-all duration-500">
                <img src="/images/talk.png" alt="Describe" className="w-16 h-16 object-contain" />
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-bold mb-3">1. Describe it</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Natural language processing understands context, style, and material properties from simple text prompts.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="w-24 h-24 mx-auto bg-black/40 border border-white/10 rounded-2xl flex items-center justify-center mb-8 relative z-10 group-hover:border-purple-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.3)] transition-all duration-500">
                <img src="/images/magic.png" alt="Synthesize" className="w-16 h-16 object-contain" />
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-bold mb-3">2. Synthesize</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Our neural engine constructs watertight geometry and generates high-quality 2K texture maps.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="w-24 h-24 mx-auto bg-black/40 border border-white/10 rounded-2xl flex items-center justify-center mb-8 relative z-10 group-hover:border-purple-500/50 group-hover:shadow-[0_0_30px_-5px_rgba(168,85,247,0.3)] transition-all duration-500">
                <img src="/images/download.png" alt="Export" className="w-16 h-16 object-contain" />
              </div>
              <div className="text-center px-4">
                <h3 className="text-xl font-bold mb-3">3. Export</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Export as standard .glb files ready for use in your projects.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-16">
            <Button
              onClick={() => setIsSignUpOpen(true)}
              variant="primary"
              className="px-8 py-4 text-lg rounded-full shadow-xl shadow-purple-500/20 flex items-center gap-2"
            >
              Start creating
              <ArrowRightIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* --- LIVING GALLERY --- */}
      <section className="py-20 bg-background-soft border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl font-bold mb-2">Curated artifacts</h2>
              <p className="text-muted-foreground text-sm uppercase tracking-widest">Generated by the community</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {CURATED_EXAMPLES.map((model) => (
              <ModelPreview key={model.id} model={model} />
            ))}
          </div>

          <div className="flex justify-center mt-12">
            <Button
              onClick={() => setIsSignUpOpen(true)}
              variant="primary"
              className="px-8 py-4 text-lg rounded-full shadow-xl shadow-purple-500/20 flex items-center gap-2"
            >
              Start creating
              <ArrowRightIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* --- WHY CHOOSE US --- */}
      <section className="py-24 px-6 border-b border-white/5 bg-white/[0.02]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-black uppercase tracking-tighter mb-4">Why Choose Our Studio</h2>
            <p className="text-sm font-mono text-muted-foreground uppercase tracking-widest">Built for professional workflows</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="p-8 text-center rounded-3xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/assembly-line.png" alt="Production Ready" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Production Ready</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">High-fidelity topology and textures optimized for real-time rendering engines.</p>
            </div>
            <div className="p-8 text-center rounded-3xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/papers-stack.png" alt="Full Ownership" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Full Ownership</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">100% commercial rights transfer. Your assets, your intellectual property.</p>
            </div>
            <div className="p-8 text-center rounded-3xl bg-white/5 border border-white/10 hover:border-purple-500/30 transition-colors group">
              <div className="flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <img src="/images/quickness.png" alt="Rapid Generation" className="w-20 h-20 object-contain" />
              </div>
              <h3 className="text-lg font-bold uppercase tracking-wide mb-3">Rapid Generation</h3>
              <p className="text-xs font-mono text-muted-foreground leading-relaxed">Accelerated generation pipeline. Iterate at the speed of thought.</p>
            </div>
          </div>

          <div className="flex justify-center mt-16">
            <Button
              onClick={() => setIsSignUpOpen(true)}
              variant="primary"
              className="px-8 py-4 text-lg rounded-full shadow-xl shadow-purple-500/20 flex items-center gap-2"
            >
              Get started now
              <ArrowRightIcon className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* --- SEO FAQ --- */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-12 text-center">Frequently asked questions</h2>
          <div className="space-y-4">
            {FAQ_ITEMS.map((item, idx) => (
              <div key={idx} className="border border-white/10 rounded-xl bg-white/[0.02] overflow-hidden">
                <details className="group">
                  <summary className="flex justify-between items-center p-6 cursor-pointer list-none">
                    <span className="font-medium text-white/90">{item.question}</span>
                    <ChevronDownIcon className="w-5 h-5 text-white/50 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-6 pb-6 text-muted-foreground leading-relaxed">
                    {item.answer}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- FOOTER CTA --- */}
      <section className="py-32 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-4xl font-bold mb-6">Ready to populate your world?</h2>
          <p className="text-muted-foreground mb-10">
            Join thousands of developers and artists creating assets with AI.
          </p>
          <Button onClick={() => setIsSignUpOpen(true)} variant="primary" className="px-8 py-4 text-lg rounded-full shadow-xl shadow-purple-500/20">
            Start creating now
          </Button>
        </div>
      </section>

    </div>
  );
}
